/**
 * This file is part of the Goobi viewer - a content presentation and management
 * application for digitized objects.
 * 
 * Visit these websites for more information.
 *          - https://goobi.io
 *          - https://www.intranda.com
 *          - https://github.com/intranda/goobi
 * 
 * This program is free software; you can redistribute it and/or modify it under the terms
 * of the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A
 * PARTICULAR PURPOSE. See the GNU General Public License for more details.
 * 
 * You should have received a copy of the GNU General Public License along with this
 * program. If not, see <http://www.gnu.org/licenses/>.
 * 
 * Module which handles the image controls of the image view.
 * 
 * @version 3.2.0
 * @module viewImage.tools.areaSelect
 * @requires jQuery
 */

import Transform from "./Transform.js";
import Draw from "./Draw.js";
import Overlay from "../overlays/Overlay.js";
import { Subject } from "rxjs";
import OpenSeadragon from "openseadragon";

const _debug = false;
const _config = {
    removeOldOverlays : true,
    drawCondition: event => event.shiftKey,
    transformCondition: event => true,
    rotateWithImage: true,
    allowDragging: true,
    drawStyle : { 
        borderColor: "green",
        borderWidth: 2,
        fillOpacity: 0.5,
        className: "imageview-overlay"
    }
}

export default class AreaSelect {
    
    constructor(viewer, config) {
        this.viewer = viewer;
        this.config = $.extend(true, {}, _config, config);
        if(_debug)console.log("config", this.config, _config, config);
        this.overlays = [];
        this.currentOverlay = undefined;
        this.finishedHook = new Subject();

        const drawConfig = {
            rotateWithImage: this.config.rotateWithImage,
            style: this.config.drawStyle
        };
        this.drawer = new Draw(viewer, drawConfig, event => this.config.drawCondition(event) && history.currentRect == undefined);
        
        this.drawer.finishedDrawing().subscribe((rect) => {
        	const overlayConfig = {
        		rotateWithImage: this.config.rotateWithImage,
        		style: this.config.drawStyle
        	}
            this.currentOverlay = new Overlay(rect.bounds.viewport, overlayConfig);
            this.currentOverlay.draw(this.viewer);
            if(_debug)console.log("created overlay ", this.currentOverlay, "create transformer");
            if(this.transformer) {
                this.transformer.close();
            }
            this.transformer = this.createTransformer(this.currentOverlay);
            if(this.config.removeOldOverlays) {
                let old = this.overlays.pop();
                if(old) {
                    old.remove(); 
                }
            }
            this.overlays.push(this.currentOverlay);

            let imageArea1 = ImageView.CoordinateConversion.convertRectFromOpenSeadragonToImage(rect.bounds.viewport, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
            let imageArea = ImageView.CoordinateConversion.convertImageAreaFromRotatedImageToImage(imageArea1, this.viewer.getRotation(), this.viewer.getOriginalImageSize());
            this.finishedHook.next(imageArea);
        });
    }

    draw(area) {
        let imageArea = new OpenSeadragon.Rect(area.x, area.y, area.width, area.height);
        let imageAreaRotated = ImageView.CoordinateConversion.convertRectFromImageToRotatedImage(imageArea, this.viewer.openseadragon);
        let imageAreaViewport = ImageView.CoordinateConversion.convertRectFromImageToOpenSeadragon(imageAreaRotated, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
        if(_debug)console.log("draw area ", imageArea, imageAreaRotated, imageAreaViewport);
        this.drawer.finishedDrawing().next({bounds:{viewport: imageAreaViewport}});
    }

    close() {
        if(_debug)console.log("closing areaselect", this);
        this.finishedHook.complete();
        this.finishedHook.unsubscribe();
        this.drawer.close(); 
        this.transformer?.close();
        this.removeOverlays();
    }

    removeOverlays() {
        if(_debug)console.log(`removing {this.overlays.length} overlays`);
        this.currentOverlay = undefined;
        this.overlays.forEach(o => o.remove());
        this.overlays = [];
    }

    createTransformer(overlay) {
        const transform = new Transform(this.viewer, overlay, {
            startCondition: this.config.transformCondition,
            allowDragging: this.config.allowDragging
        });

        transform.finishedTransforming().subscribe((o) => {
            if(o) {
                const imageArea = ImageView.CoordinateConversion.convertRectFromOpenSeadragonToImage(o.bounds, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
                const rotatedImageArea = ImageView.CoordinateConversion.convertImageAreaFromRotatedImageToImage(imageArea, this.viewer.getRotation(), this.viewer.getOriginalImageSize());
                this.finishedHook.next(rotatedImageArea); 
            }
        });

        return transform;
    }
}